import { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "primereact/button";
import { Badge } from "primereact/badge";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Dropdown } from "primereact/dropdown";
import { MultiSelect } from "primereact/multiselect";
import { InputText } from "primereact/inputtext";
import { Divider } from "primereact/divider";
import { useQueryClient } from "react-query";
import { appToastContext } from "../../context/toast";
import { useTallyForm } from "../../api/queries/tally/useTallyForm";
import { useTallyFormQuestions } from "../../api/queries/tally/useTallyFormQuestions";
import { useUpdateMappingTags } from "../../api/queries/tally/useUpdateMappingTags";
import { useTallyMapping } from "../../api/queries/tally/useTallyMapping";
import { useFieldTypesFlat } from "../../api/queries/field-types/useFieldTypesFlat";
import { useTagList } from "../../api/queries/tag/list";
import { queryKeys } from "../../api/query";
import {
  TallyFormDetail as TallyFormDetailType,
  TallyMappingPayload,
  TALLY_MAPPING_FIELDS,
  TallyQuestion,
  TallyQuestionMappingResponse,
} from "../../api/types/tally";
import {
  DataTypeEnum,
  DataTypeLabels,
  FieldType,
} from "../../api/types/field-type";
import MappingCompatibilityHelp from "./MappingCompatibilityHelp";
import { isTallyMappingCompatible } from "./compatibility";
import { getTallyQuestionType } from "./questionType";

interface FieldTypeOption {
  label: string;
  value: string;
  type: string;
  required: boolean;
}

interface EditableTallyQuestionMappingResponse extends TallyQuestionMappingResponse {
  selectedFieldTypeId: string | null;
  fieldTypesVersion: string;
}

interface UnmappedTallyQuestionRow extends TallyQuestion {
  selectedFieldTypeId: string | null;
  selectedTagIds: string[];
}

export default function TallyFormDetail() {
  const navigate = useNavigate();
  const { id } = useParams();
  const queryClient = useQueryClient();
  const { ref: appToastRef } = useContext(appToastContext);

  const { data: formResponse, isLoading } = useTallyForm(id);
  const form: TallyFormDetailType | undefined = formResponse?.data?.form;
  const formRecordId = form?.id;

  const tallyFormQuestions = useTallyFormQuestions();
  const updateMappingTags = useUpdateMappingTags();
  const tallyMapping = useTallyMapping();

  const { data: tagListResponse } = useTagList();

  const [mappedTagSelections, setMappedTagSelections] = useState<
    Record<string, string[]>
  >({});
  const [mappedFieldTypes, setMappedFieldTypes] = useState<
    Record<string, string | null>
  >({});
  const [unmappedQuestions, setUnmappedQuestions] = useState<TallyQuestion[]>(
    [],
  );
  const [unmappedFieldTypes, setUnmappedFieldTypes] = useState<
    Record<string, string | null>
  >({});
  const [unmappedTags, setUnmappedTags] = useState<Record<string, string[]>>(
    {},
  );
  const [invalidMappedIds, setInvalidMappedIds] = useState<Set<string>>(
    new Set(),
  );
  const [invalidUnmappedQuestionIds, setInvalidUnmappedQuestionIds] = useState<
    Set<string>
  >(new Set());
  const [hasReloaded, setHasReloaded] = useState(false);
  const [isReloading, setIsReloading] = useState(false);
  const [savingQuestionId, setSavingQuestionId] = useState<string | null>(null);
  const [savingMappedQuestionId, setSavingMappedQuestionId] = useState<
    string | null
  >(null);
  const [mappedSearch, setMappedSearch] = useState("");
  const [unmappedSearch, setUnmappedSearch] = useState("");

  const {
    data: fieldTypesResponse,
    isLoading: isFieldTypesLoading,
    refetch: refetchFieldTypes,
  } = useFieldTypesFlat({ enabled: false });

  const fieldTypeOptions = useMemo<FieldTypeOption[]>(() => {
    const buildFieldTypeLabel = (fieldType: FieldType): string => {
      const segments: string[] = [];
      if (fieldType.parent?.name) {
        segments.push(fieldType.parent.name);
      }
      segments.push(fieldType.name);
      return segments.join(" ▸ ");
    };

    return (
      fieldTypesResponse?.data?.map((fieldType: FieldType) => ({
        label: buildFieldTypeLabel(fieldType),
        value: fieldType.id,
        type: fieldType.type,
        required: fieldType.required,
      })) ?? []
    );
  }, [fieldTypesResponse?.data]);

  const fieldTypeOptionsByValue = useMemo(
    () =>
      new Map(
        fieldTypeOptions.map((option) => [option.value, option] as const),
      ),
    [fieldTypeOptions],
  );

  const tagOptions = useMemo(
    () =>
      tagListResponse?.data?.results?.map((tag) => ({
        label: tag.name,
        value: tag.id,
      })) ?? [],
    [tagListResponse?.data?.results],
  );

  const filteredMappings = useMemo(() => {
    if (!form?.mappings) return [];
    if (!mappedSearch.trim()) return form.mappings;

    const search = mappedSearch.toLowerCase();
    return form.mappings.filter(
      (mapping) =>
        mapping.questionTitle.toLowerCase().includes(search) ||
        mapping.questionId.toLowerCase().includes(search) ||
        mapping.questionType.toLowerCase().includes(search),
    );
  }, [form?.mappings, mappedSearch]);

  const editableMappings = useMemo<EditableTallyQuestionMappingResponse[]>(
    () =>
      filteredMappings.map((mapping) => ({
        ...mapping,
        selectedFieldTypeId:
          mappedFieldTypes[mapping.id] ?? mapping.fieldTypeId,
        fieldTypesVersion: `${fieldTypeOptions.length}-${isFieldTypesLoading ? "loading" : "ready"}`,
      })),
    [
      filteredMappings,
      mappedFieldTypes,
      fieldTypeOptions.length,
      isFieldTypesLoading,
    ],
  );

  const filteredUnmappedQuestions = useMemo(() => {
    if (!unmappedSearch.trim()) return unmappedQuestions;

    const search = unmappedSearch.toLowerCase();
    return unmappedQuestions.filter(
      (question) =>
        question.title?.toLowerCase().includes(search) ||
        question.id.toLowerCase().includes(search) ||
        getTallyQuestionType(question).toLowerCase().includes(search),
    );
  }, [unmappedQuestions, unmappedSearch]);

  const unmappedQuestionRows = useMemo<UnmappedTallyQuestionRow[]>(
    () =>
      filteredUnmappedQuestions.map((question) => ({
        ...question,
        selectedFieldTypeId: unmappedFieldTypes[question.id] ?? null,
        selectedTagIds: unmappedTags[question.id] ?? [],
      })),
    [filteredUnmappedQuestions, unmappedFieldTypes, unmappedTags],
  );

  useEffect(() => {
    if (!form?.mappings) {
      setMappedTagSelections({});
      setMappedFieldTypes({});
      setInvalidMappedIds(new Set());
      return;
    }

    const nextTagSelections: Record<string, string[]> = {};
    const nextFieldTypes: Record<string, string> = {};
    form.mappings.forEach((mapping) => {
      nextTagSelections[mapping.id] = mapping.tags.map((tag) => tag.id);
      nextFieldTypes[mapping.id] = mapping.fieldTypeId;
    });
    setMappedTagSelections(nextTagSelections);
    setMappedFieldTypes(nextFieldTypes);
    setInvalidMappedIds(new Set());
  }, [form?.mappings]);

  useEffect(() => {
    if (!formRecordId) {
      return;
    }

    void refetchFieldTypes();
  }, [formRecordId, refetchFieldTypes]);

  useEffect(() => {
    if (!form || unmappedQuestions.length === 0) {
      return;
    }

    const mappedIds = new Set(
      form.mappings.map((mapping) => mapping.questionId),
    );
    setUnmappedQuestions((prev) =>
      prev.filter((question) => !mappedIds.has(question.id)),
    );
  }, [form, form?.mappings, unmappedQuestions.length]);

  const showToast = (
    severity: "success" | "info" | "warn" | "error",
    summary: string,
  ) => {
    if (!appToastRef?.current) {
      return;
    }

    appToastRef.current.show({
      severity,
      summary,
    });
  };

  const formatQuestionType = (type: string) => type.replace(/_/g, " ");

  const mappedQuestionTemplate = (
    rowData: EditableTallyQuestionMappingResponse,
  ) => (
    <div className="flex flex-column">
      <span className="font-semibold">{rowData.questionTitle}</span>
      <small className="text-500">Question ID: {rowData.questionId}</small>
    </div>
  );

  const mappedTypeTemplate = (
    rowData: EditableTallyQuestionMappingResponse,
  ) => (
    <Badge value={formatQuestionType(rowData.questionType)} severity="info" />
  );

  const renderFieldTypeOption = (option: FieldTypeOption | undefined | null) =>
    option ? (
      <div className="flex flex-column">
        <span className="font-semibold">{option.label}</span>
        <small className="text-500 uppercase">
          {option.type} {option.required ? "• Required" : ""}
        </small>
      </div>
    ) : null;

  const resolveFieldTypeOption = (
    option: FieldTypeOption | string | undefined | null,
  ) => {
    if (!option) {
      return null;
    }

    return typeof option === "string"
      ? (fieldTypeOptionsByValue.get(option) ?? null)
      : option;
  };

  const handleMappedFieldTypeChange = (
    mappingId: string,
    value: string | null | undefined,
  ) => {
    setMappedFieldTypes((prev) => ({
      ...prev,
      [mappingId]: value ?? null,
    }));
    setInvalidMappedIds((prev) => {
      if (!prev.has(mappingId)) {
        return prev;
      }

      const next = new Set(prev);
      next.delete(mappingId);
      return next;
    });
  };

  const isMappedFieldTypeInvalid = (
    rowData: EditableTallyQuestionMappingResponse,
  ): boolean => {
    if (!rowData.selectedFieldTypeId) {
      return false;
    }

    const selectedFieldType = fieldTypeOptionsByValue.get(
      rowData.selectedFieldTypeId,
    );

    if (!selectedFieldType) {
      return invalidMappedIds.has(rowData.id) || rowData.isTypeMismatch;
    }

    return (
      invalidMappedIds.has(rowData.id) ||
      !isTallyMappingCompatible(rowData.questionType, selectedFieldType.type)
    );
  };

  const mappedFieldTypeTemplate = (
    rowData: EditableTallyQuestionMappingResponse,
  ) => {
    const fieldTypeLabel = rowData.fieldTypeParentName
      ? `${rowData.fieldTypeParentName} ▸ ${rowData.fieldTypeName}`
      : rowData.fieldTypeName;

    const dataTypeLabel =
      DataTypeLabels[rowData.fieldTypeDataType as DataTypeEnum] ??
      rowData.fieldTypeDataType;

    if (rowData.isTypeMismatch) {
      const isInvalid = isMappedFieldTypeInvalid(rowData);

      return (
        <div className="flex flex-column gap-2">
          <span className="p-error flex align-items-center">
            <i className="pi pi-exclamation-triangle mr-2" />
            {fieldTypeLabel} <small>({dataTypeLabel})</small>
          </span>
          <Dropdown
            key={`${rowData.id}-${rowData.fieldTypesVersion}`}
            value={rowData.selectedFieldTypeId}
            options={fieldTypeOptions}
            optionLabel="label"
            optionValue="value"
            onChange={(event) =>
              handleMappedFieldTypeChange(rowData.id, event.value)
            }
            placeholder="Select field type"
            className={`w-full ${isInvalid ? "p-invalid" : ""}`}
            filter
            filterBy="label"
            loading={isFieldTypesLoading}
            emptyMessage={
              isFieldTypesLoading
                ? "Loading field types..."
                : "No field types found"
            }
            itemTemplate={(option) => renderFieldTypeOption(option)}
            valueTemplate={(option, props) =>
              resolveFieldTypeOption(option) ? (
                renderFieldTypeOption(resolveFieldTypeOption(option))
              ) : (
                <span className="text-400">{props.placeholder}</span>
              )
            }
          />
          {isInvalid && (
            <small className="p-error">
              Incompatible with {rowData.questionType}
            </small>
          )}
        </div>
      );
    }

    return (
      <span>
        {fieldTypeLabel} <small className="text-500">({dataTypeLabel})</small>
      </span>
    );
  };

  const handleMappedTagsChange = async (
    mapping: TallyQuestionMappingResponse,
    tagIds: string[],
  ) => {
    setMappedTagSelections((prev) => ({
      ...prev,
      [mapping.id]: tagIds,
    }));

    try {
      await updateMappingTags.mutateAsync({
        mappingId: mapping.id,
        tagIds,
        formId: form?.id ?? "",
      });
      showToast("success", "Tags updated.");
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ?? "Unable to update tags.";
      showToast("error", errorMessage);
    }
  };

  const mappedTagsTemplate = (
    rowData: EditableTallyQuestionMappingResponse,
  ) => (
    <MultiSelect
      value={mappedTagSelections[rowData.id] ?? []}
      options={tagOptions}
      onChange={(event) => handleMappedTagsChange(rowData, event.value)}
      placeholder="Select tags"
      className="w-full"
      display="chip"
      filter
      maxSelectedLabels={4}
    />
  );

  const handleSaveMappedQuestion = async (
    mapping: EditableTallyQuestionMappingResponse,
  ) => {
    if (!form) {
      return;
    }

    const fieldTypeId = mappedFieldTypes[mapping.id] ?? mapping.fieldTypeId;
    if (!fieldTypeId) {
      showToast("warn", "Select a field type before saving.");
      return;
    }

    const selectedFieldType = fieldTypeOptionsByValue.get(fieldTypeId);
    if (
      selectedFieldType &&
      !isTallyMappingCompatible(mapping.questionType, selectedFieldType.type)
    ) {
      setInvalidMappedIds((prev) => new Set(prev).add(mapping.id));
      showToast("error", "Selected field type is incompatible.");
      return;
    }

    if (!form.accountId) {
      showToast("error", "Account information is missing for this form.");
      return;
    }

    const payload: TallyMappingPayload = {
      [TALLY_MAPPING_FIELDS.ACCOUNT_ID]: form.accountId,
      [TALLY_MAPPING_FIELDS.FORM_ID]: form.formId,
      [TALLY_MAPPING_FIELDS.FORM_NAME]: form.name,
      mappings: [
        {
          [TALLY_MAPPING_FIELDS.QUESTION_ID]: mapping.questionId,
          [TALLY_MAPPING_FIELDS.QUESTION_TITLE]: mapping.questionTitle,
          [TALLY_MAPPING_FIELDS.QUESTION_TYPE]: mapping.questionType,
          [TALLY_MAPPING_FIELDS.FIELD_TYPE_ID]: fieldTypeId,
          tag_ids:
            mappedTagSelections[mapping.id] ??
            mapping.tags.map((tag) => tag.id),
        },
      ],
    };

    setSavingMappedQuestionId(mapping.questionId);
    try {
      await tallyMapping.mutateAsync(payload);
      showToast("success", "Mapping updated.");
      queryClient.invalidateQueries(queryKeys.tallyForm(form.id));
    } catch (error: any) {
      queryClient.invalidateQueries(queryKeys.tallyForm(form.id));
      const errorMessage =
        error?.response?.data?.message ?? "Unable to save mapping.";
      showToast("error", errorMessage);
    } finally {
      setSavingMappedQuestionId(null);
    }
  };

  const mappedActionsTemplate = (
    rowData: EditableTallyQuestionMappingResponse,
  ) => {
    if (!rowData.isTypeMismatch) {
      return <span className="text-400">-</span>;
    }

    return (
      <Button
        label="Save"
        icon="pi pi-check"
        className="p-button-sm"
        loading={savingMappedQuestionId === rowData.questionId}
        disabled={
          !rowData.selectedFieldTypeId ||
          rowData.selectedFieldTypeId === rowData.fieldTypeId ||
          isMappedFieldTypeInvalid(rowData)
        }
        onClick={() => handleSaveMappedQuestion(rowData)}
      />
    );
  };

  const questionTitleTemplate = (rowData: TallyQuestion) => (
    <div>
      <div className="font-semibold">{rowData.title || "-"}</div>
      {rowData.description && (
        <small className="text-500">{rowData.description}</small>
      )}
    </div>
  );

  const questionTypeTemplate = (rowData: TallyQuestion) => (
    <Badge
      value={formatQuestionType(getTallyQuestionType(rowData))}
      severity="info"
    />
  );

  const handleReloadQuestions = async () => {
    if (!form) {
      return;
    }

    setIsReloading(true);
    try {
      const response = await tallyFormQuestions.mutateAsync(form.formId);
      const questions = response.data.questions ?? [];
      const mappedIds = new Set(
        form.mappings.map((mapping) => mapping.questionId),
      );
      const nextUnmapped = questions.filter(
        (question) => !mappedIds.has(question.id),
      );

      setUnmappedQuestions(nextUnmapped);
      setHasReloaded(true);
      setInvalidUnmappedQuestionIds(new Set());

      setUnmappedFieldTypes((prev) => {
        const next: Record<string, string | null> = {};
        nextUnmapped.forEach((question) => {
          next[question.id] = prev[question.id] ?? null;
        });
        return next;
      });

      setUnmappedTags((prev) => {
        const next: Record<string, string[]> = {};
        nextUnmapped.forEach((question) => {
          next[question.id] = prev[question.id] ?? [];
        });
        return next;
      });
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ?? "Unable to reload questions.";
      showToast("error", errorMessage);
    } finally {
      setIsReloading(false);
    }
  };

  const handleUnmappedFieldTypeChange = (
    questionId: string,
    value: string | null | undefined,
  ) => {
    setUnmappedFieldTypes((prev) => ({
      ...prev,
      [questionId]: value ?? null,
    }));
    setInvalidUnmappedQuestionIds((prev) => {
      if (!prev.has(questionId)) {
        return prev;
      }

      const next = new Set(prev);
      next.delete(questionId);
      return next;
    });
  };

  const handleUnmappedTagsChange = (questionId: string, value: string[]) => {
    setUnmappedTags((prev) => ({
      ...prev,
      [questionId]: value,
    }));
  };

  const isUnmappedFieldTypeInvalid = (
    rowData: UnmappedTallyQuestionRow,
  ): boolean => {
    if (!rowData.selectedFieldTypeId) {
      return false;
    }

    const selectedFieldType = fieldTypeOptionsByValue.get(
      rowData.selectedFieldTypeId,
    );

    if (!selectedFieldType) {
      return invalidUnmappedQuestionIds.has(rowData.id);
    }

    return (
      invalidUnmappedQuestionIds.has(rowData.id) ||
      !isTallyMappingCompatible(
        getTallyQuestionType(rowData),
        selectedFieldType.type,
      )
    );
  };

  const fieldTypeSelectorTemplate = (rowData: UnmappedTallyQuestionRow) => {
    const isInvalid = isUnmappedFieldTypeInvalid(rowData);

    return (
      <div className="flex flex-column gap-1">
        <Dropdown
          value={rowData.selectedFieldTypeId}
          options={fieldTypeOptions}
          optionLabel="label"
          optionValue="value"
          onChange={(event) =>
            handleUnmappedFieldTypeChange(rowData.id, event.value)
          }
          placeholder="Select field type"
          className={`w-full ${isInvalid ? "p-invalid" : ""}`}
          showClear
          filter
          filterBy="label"
          loading={isFieldTypesLoading}
          emptyMessage={
            isFieldTypesLoading
              ? "Loading field types..."
              : "No field types found"
          }
          itemTemplate={(option) => renderFieldTypeOption(option)}
          valueTemplate={(option, props) => {
            const selectedOption = resolveFieldTypeOption(option);

            if (selectedOption) {
              return renderFieldTypeOption(selectedOption);
            }

            return <span className="text-400">{props.placeholder}</span>;
          }}
        />
        {isInvalid && (
          <small className="p-error">
            Incompatible with {getTallyQuestionType(rowData)}
          </small>
        )}
      </div>
    );
  };

  const unmappedTagsTemplate = (rowData: UnmappedTallyQuestionRow) => (
    <MultiSelect
      value={rowData.selectedTagIds}
      options={tagOptions}
      onChange={(event) => handleUnmappedTagsChange(rowData.id, event.value)}
      placeholder="Select tags"
      className="w-full"
      display="chip"
      filter
      maxSelectedLabels={4}
    />
  );

  const handleSaveUnmappedQuestion = async (question: TallyQuestion) => {
    if (!form) {
      return;
    }

    const fieldTypeId = unmappedFieldTypes[question.id];
    if (!fieldTypeId) {
      showToast("warn", "Select a field type before saving.");
      return;
    }

    const selectedFieldType = fieldTypeOptionsByValue.get(fieldTypeId);
    if (
      selectedFieldType &&
      !isTallyMappingCompatible(
        getTallyQuestionType(question),
        selectedFieldType.type,
      )
    ) {
      setInvalidUnmappedQuestionIds((prev) => new Set(prev).add(question.id));
      showToast("error", "Selected field type is incompatible.");
      return;
    }

    if (!form.accountId) {
      showToast("error", "Account information is missing for this form.");
      return;
    }

    const payload: TallyMappingPayload = {
      [TALLY_MAPPING_FIELDS.ACCOUNT_ID]: form.accountId,
      [TALLY_MAPPING_FIELDS.FORM_ID]: form.formId,
      [TALLY_MAPPING_FIELDS.FORM_NAME]: form.name,
      mappings: [
        {
          [TALLY_MAPPING_FIELDS.QUESTION_ID]: question.id,
          [TALLY_MAPPING_FIELDS.QUESTION_TITLE]: question.title,
          [TALLY_MAPPING_FIELDS.QUESTION_TYPE]: getTallyQuestionType(question),
          [TALLY_MAPPING_FIELDS.FIELD_TYPE_ID]: fieldTypeId,
          tag_ids: unmappedTags[question.id] ?? [],
        },
      ],
    };

    setSavingQuestionId(question.id);
    try {
      await tallyMapping.mutateAsync(payload);
      showToast("success", "Mapping saved.");
      setUnmappedQuestions((prev) =>
        prev.filter((item) => item.id !== question.id),
      );
      setUnmappedFieldTypes((prev) => {
        const { [question.id]: _, ...rest } = prev;
        return rest;
      });
      setUnmappedTags((prev) => {
        const { [question.id]: _, ...rest } = prev;
        return rest;
      });
      queryClient.invalidateQueries(queryKeys.tallyForm(form.id));
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ?? "Unable to save mapping.";
      showToast("error", errorMessage);
    } finally {
      setSavingQuestionId(null);
    }
  };

  const unmappedActionsTemplate = (rowData: TallyQuestion) => (
    <Button
      label="Save"
      icon="pi pi-check"
      className="p-button-sm"
      loading={savingQuestionId === rowData.id}
      onClick={() => handleSaveUnmappedQuestion(rowData)}
    />
  );

  if (!isLoading && !form) {
    return (
      <div className="col-12">
        <div className="flex align-items-center gap-3 mb-4">
          <Button
            icon="pi pi-arrow-left"
            className="p-button-text"
            onClick={() => navigate("/tally/forms")}
          />
          <h1 className="m-0">Tally Form</h1>
        </div>
        <div className="card p-4">
          <p className="m-0">Form not found.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="col-12">
      <div className="flex align-items-center gap-3 py-3">
        <Button
          icon="pi pi-arrow-left"
          className="p-button-text"
          onClick={() => navigate("/tally/forms")}
        />
        <div>
          <h1 className="m-0">
            {form?.name ?? "Tally Form"}
            {form?.formId && (
              <span className="text-500 font-normal"> ({form.formId})</span>
            )}
          </h1>
          {form?.formId && (
            <a
              href={`https://tally.so/r/${form.formId}`}
              target="_blank"
              rel="noreferrer"
              className="text-500"
            >
              View in Tally
            </a>
          )}
        </div>
      </div>

      <div className="card p-4">
        <div className="flex align-items-center gap-2">
          <h2 className="m-0 text-lg">Mapped Questions</h2>
          <MappingCompatibilityHelp />
        </div>
        <p className="text-500 mt-2 mb-4">
          Field types can be changed only for red incompatible mappings. Tags
          can be updated anytime.
        </p>

        <div className="mb-3">
          <span className="p-input-icon-left w-full md:w-20rem">
            <i className="pi pi-search ml-2" />
            <InputText
              value={mappedSearch}
              onChange={(e) => setMappedSearch(e.target.value)}
              placeholder="Search by title, ID, or type..."
              className="w-full pl-5"
            />
          </span>
        </div>

        <DataTable
          value={editableMappings}
          loading={isLoading}
          emptyMessage={
            mappedSearch
              ? "No matching questions found."
              : "No mapped questions yet."
          }
          stripedRows
          responsiveLayout="scroll"
          rowClassName={(rowData: EditableTallyQuestionMappingResponse) =>
            rowData.isTypeMismatch || isMappedFieldTypeInvalid(rowData)
              ? "tally-invalid-row"
              : ""
          }
        >
          <Column header="Question" body={mappedQuestionTemplate} />
          <Column
            header="Type"
            body={mappedTypeTemplate}
            style={{ width: "10rem" }}
          />
          <Column header="Field Type" body={mappedFieldTypeTemplate} />
          <Column
            header="Tags"
            body={mappedTagsTemplate}
            style={{ width: "22rem" }}
          />
          <Column
            header="Actions"
            body={mappedActionsTemplate}
            style={{ width: "8rem" }}
          />
        </DataTable>

        <Divider />

        <h2 className="m-0 text-lg">Unmapped Questions</h2>
        <p className="text-500 mt-2 mb-4">
          Reload questions from Tally to map any new fields.
        </p>
        <Button
          label="Reload Tally Questions"
          icon="pi pi-refresh"
          className="p-button-outlined"
          loading={isReloading}
          onClick={handleReloadQuestions}
        />

        {hasReloaded && (
          <div className="mt-4">
            <div className="mb-3">
              <span className="p-input-icon-left w-full md:w-20rem">
                <i className="pi pi-search ml-2" />
                <InputText
                  value={unmappedSearch}
                  onChange={(e) => setUnmappedSearch(e.target.value)}
                  placeholder="Search by title, ID, or type..."
                  className="w-full pl-5"
                />
              </span>
            </div>

            <DataTable
              value={unmappedQuestionRows}
              loading={isReloading}
              emptyMessage={
                unmappedSearch
                  ? "No matching questions found."
                  : "No unmapped questions found."
              }
              stripedRows
              responsiveLayout="scroll"
              rowClassName={(rowData: UnmappedTallyQuestionRow) =>
                isUnmappedFieldTypeInvalid(rowData) ? "tally-invalid-row" : ""
              }
            >
              <Column header="Question" body={questionTitleTemplate} />
              <Column
                header="Type"
                body={questionTypeTemplate}
                style={{ width: "10rem" }}
              />
              <Column header="Field Type" body={fieldTypeSelectorTemplate} />
              <Column
                header="Tags"
                body={unmappedTagsTemplate}
                style={{ width: "22rem" }}
              />
              <Column
                header="Actions"
                body={unmappedActionsTemplate}
                style={{ width: "8rem" }}
              />
            </DataTable>
          </div>
        )}
      </div>
    </div>
  );
}
